import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { buildEchartsTheme } from '../echarts-theme';

export interface IEchartsHeatmapDatum {
  x: string | number;
  y: string | number;
  value: number;
}

@Component({
  selector: 'pr-echarts-heatmap',
  templateUrl: './echarts-heatmap.component.html',
  styleUrls: ['./echarts-heatmap.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
})
export class EchartsHeatmapComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly data = input<IEchartsHeatmapDatum[]>([]);
  readonly xAxisLabels = input<(string | number)[]>([]);
  readonly yAxisLabels = input<(string | number)[]>();
  readonly label = input.required<string>();
  readonly height = input<number>(200);
  readonly ariaLabelOverride = input<string>();

  readonly hasData = computed(() => this.data().length > 0);

  readonly ariaLabel = computed(() => {
    const override = this.ariaLabelOverride();
    if (override) {
      return override;
    }
    const cells = this.data();
    if (cells.length === 0) {
      return this.label();
    }
    const maxCell = cells.reduce((max, cell) =>
      cell.value > max.value ? cell : max
    );
    return `${this.label()} heatmap, ${cells.length} cells, peak at ${
      maxCell.x
    } with value ${maxCell.value}`;
  });

  readonly chartOptions: Signal<EChartsOption> = computed(() => {
    const cells = this.data();
    if (cells.length === 0) {
      return {};
    }

    const theme = buildEchartsTheme(this._elementRef.nativeElement);
    const xLabels = this.xAxisLabels();
    const yLabels = this.yAxisLabels() ?? [''];

    const values = cells.map((cell) => cell.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    const seriesData = cells.map((cell) => [cell.x, cell.y ?? '', cell.value]);

    const palette = Array.isArray(theme.color) ? theme.color : [];
    const primaryColour = typeof palette[0] === 'string' ? palette[0] : '';

    return {
      ...theme,
      grid: {
        ...theme.grid,
        bottom: 48,
      },
      xAxis: {
        type: 'category',
        data: xLabels,
        splitArea: { show: true },
        axisLabel: { fontSize: 11 },
      },
      yAxis: {
        type: 'category',
        data: yLabels,
        splitArea: { show: true },
        axisLabel: { fontSize: 11 },
      },
      visualMap: {
        min: minValue,
        max: maxValue,
        calculable: false,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        inRange: {
          color: [primaryColour ? `${primaryColour}20` : '', primaryColour],
        },
      },
      tooltip: {
        ...theme.tooltip,
        position: 'top',
      },
      series: [
        {
          type: 'heatmap',
          name: this.label(),
          data: seriesData,
          label: {
            show: yLabels.length <= 1,
            fontSize: 10,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 6,
            },
          },
        },
      ],
    };
  });
}
